/* ==========================================================================
   XELPHORA — global.js
   Vanilla ES6+, no dependencies, loaded with `defer`.

   Everything is either a custom element or an event-delegated singleton, so
   Shopify's theme editor can add, move and remove sections and the behaviour
   re-binds itself with no page reload (see the shopify:section:* hooks at the
   bottom of the file).
   ========================================================================== */

(() => {
  'use strict';

  const Theme = (window.Xelphora = window.Xelphora || {});
  const routes = Theme.routes || {};
  const strings = Theme.strings || {};
  const config = Theme.settings || {};

  /* ========================================================================
     1. Utilities
     ======================================================================== */

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Two frames: the first flushes the style change, the second animates it. */
  const nextFrame = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

  function debounce(fn, wait = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  function focusableWithin(root) {
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );
  }

  /**
   * Waits for a CSS transition on `el` to finish, with a timeout fallback so a
   * cancelled or absent transition can never leave a drawer half-open.
   */
  function afterTransition(el, callback) {
    const duration = Number(config.motionDuration) || 450;
    if (!el || reduceMotion()) {
      callback();
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      callback();
    };
    const onEnd = (event) => {
      if (event.target === el) finish();
    };

    el.addEventListener('transitionend', onEnd);
    setTimeout(finish, duration + 80);
  }

  /* ------------------------------------------------------------------------
     Focus trap — keeps Tab inside an open dialog and restores focus on close.
     ------------------------------------------------------------------------ */
  class FocusTrap {
    constructor(container) {
      this.container = container;
      this.previous = null;
      this.onKeydown = this.onKeydown.bind(this);
    }

    activate(focusTarget) {
      this.previous = document.activeElement;
      document.addEventListener('keydown', this.onKeydown);

      const target = focusTarget || focusableWithin(this.container)[0] || this.container;
      // Let the open transition start before stealing focus, or Safari scrolls.
      nextFrame(() => target.focus({ preventScroll: true }));
    }

    deactivate() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.previous && typeof this.previous.focus === 'function') {
        this.previous.focus({ preventScroll: true });
      }
      this.previous = null;
    }

    onKeydown(event) {
      if (event.key !== 'Tab') return;

      const items = focusableWithin(this.container);
      if (!items.length) {
        event.preventDefault();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /* ------------------------------------------------------------------------
     Scroll lock — reference counted, and compensates for the scrollbar so
     locking never shifts the layout (a CLS source in most themes).
     ------------------------------------------------------------------------ */
  const ScrollLock = {
    depth: 0,

    lock() {
      if (this.depth === 0) {
        const gap = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--scrollbar-width', `${gap}px`);
        document.body.classList.add('overflow-hidden');
      }
      this.depth += 1;
    },

    unlock() {
      this.depth = Math.max(0, this.depth - 1);
      if (this.depth === 0) {
        document.body.classList.remove('overflow-hidden');
        document.documentElement.style.removeProperty('--scrollbar-width');
      }
    },
  };

  /* ========================================================================
     2. Header metrics
     Publishes the real header and announcement heights as CSS variables so
     sticky offsets, scroll-padding and transparent heroes stay exact at any
     font size or announcement length.
     ======================================================================== */
  const HeaderMetrics = {
    init() {
      this.measure();

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.measure());
        this.observeTargets();
      }
      window.addEventListener('resize', debounce(() => this.measure(), 150));
    },

    observeTargets() {
      if (!this.observer) return;
      this.observer.disconnect();
      document.querySelectorAll('.header, .announcement').forEach((el) => this.observer.observe(el));
    },

    measure() {
      const header = document.querySelector('.header');
      const announcement = document.querySelector('.announcement');
      const root = document.documentElement;

      if (header) root.style.setProperty('--header-height', `${Math.round(header.offsetHeight)}px`);
      root.style.setProperty(
        '--announcement-height',
        `${announcement ? Math.round(announcement.offsetHeight) : 0}px`
      );
    },
  };

  /* ========================================================================
     3. Drawer manager
     One delegated controller for every [data-drawer] element: menu, search,
     cart, and anything a later section adds.
     ======================================================================== */
  const Drawers = {
    open: null,
    traps: new WeakMap(),

    init() {
      document.addEventListener('click', this.onClick.bind(this));
      document.addEventListener('keydown', this.onKeydown.bind(this));

      // Sections may ask for a drawer without knowing about this module.
      document.addEventListener('drawer:open', (e) => this.show(e.detail.name, e.detail.trigger));
      document.addEventListener('drawer:close', () => this.hide());
    },

    get(name) {
      return document.querySelector(`[data-drawer="${name}"]`);
    },

    onClick(event) {
      const toggle = event.target.closest(
        '[data-menu-toggle], [data-search-toggle], [data-cart-toggle]'
      );
      if (toggle) {
        // Never intercept the cart link when the merchant chose a cart page.
        if (toggle.tagName === 'A') return;
        event.preventDefault();
        const name = toggle.hasAttribute('data-menu-toggle')
          ? 'menu'
          : toggle.hasAttribute('data-search-toggle')
            ? 'search'
            : 'cart';
        this.open === name ? this.hide() : this.show(name, toggle);
        return;
      }

      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.hide();
      }
    },

    onKeydown(event) {
      if (event.key === 'Escape' && this.open) {
        event.stopPropagation();
        this.hide();
      }
    },

    show(name, trigger) {
      if (this.open) this.hide({ silent: true });

      const el = this.get(name);
      if (!el) return;

      this.open = name;
      this.trigger = trigger || null;

      el.removeAttribute('hidden');
      ScrollLock.lock();

      let trap = this.traps.get(el);
      if (!trap) {
        trap = new FocusTrap(el);
        this.traps.set(el, trap);
      }

      nextFrame(() => {
        el.classList.add('is-open');
        // Search should land in the input; other drawers use their first control.
        const preferred = el.querySelector('[data-search-input]');
        trap.activate(preferred || null);
      });

      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('drawer-open');
      el.dispatchEvent(new CustomEvent('drawer:opened', { bubbles: true, detail: { name } }));
    },

    hide({ silent = false } = {}) {
      const name = this.open;
      if (!name) return;

      const el = this.get(name);
      this.open = null;
      document.documentElement.classList.remove('drawer-open');

      if (!el) return;

      el.classList.remove('is-open');
      const trap = this.traps.get(el);
      if (trap && !silent) trap.deactivate();

      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger = null;
      }

      const panel = el.querySelector('[data-drawer-panel]') || el;
      afterTransition(panel, () => {
        // Guard against a re-open landing during the close transition.
        if (!el.classList.contains('is-open')) el.setAttribute('hidden', '');
      });

      ScrollLock.unlock();
      el.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true, detail: { name } }));
    },
  };

  /* ========================================================================
     4. Cart API
     Thin wrapper over Shopify's Cart AJAX API. Every mutation asks the server
     to re-render the cart-drawer section, so prices, discounts and the free
     shipping bar are never recalculated in JavaScript.
     ======================================================================== */
  const CartAPI = {
    /**
     * Which sections Shopify should re-render, discovered from whatever cart
     * hosts are on this page: the drawer, the cart page, or both. Nothing here
     * hard-codes a section name, so a new cart surface only has to declare
     * data-cart-section to join in.
     */
    get sections() {
      const names = new Set();
      document
        .querySelectorAll('[data-cart-section]')
        .forEach((node) => names.add(node.dataset.cartSection));
      return Array.from(names).join(',') || 'cart-drawer';
    },

    async post(path, body) {
      const response = await fetch(`${path}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...body,
          sections: this.sections,
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(data.description || data.message || strings.cartError);
        error.data = data;
        error.status = response.status;
        throw error;
      }

      return data;
    },

    add(items) {
      return this.post(routes.cartAdd, { items: Array.isArray(items) ? items : [items] });
    },

    change(payload) {
      return this.post(routes.cartChange, payload);
    },

    update(payload) {
      return this.post(routes.cartUpdate, payload);
    },

    async get() {
      const response = await fetch(`${routes.cart}.js`, {
        headers: { Accept: 'application/json' },
      });
      return response.json();
    },

    /** Broadcasts one canonical event so header, drawer and any future
     *  upsell widget all update from the same payload. */
    publish(cart, meta = {}) {
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart, ...meta } })
      );
    },
  };

  Theme.cart = CartAPI;
  Theme.drawers = Drawers;
  Theme.utils = { debounce, afterTransition, nextFrame, FocusTrap, ScrollLock, focusableWithin };

  /* Keep the header bubble in sync from a single place. */
  document.addEventListener('cart:updated', (event) => {
    const count = event.detail?.cart?.item_count;
    if (typeof count !== 'number') return;

    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = count;
      node.toggleAttribute('hidden', count === 0);
    });

    document.querySelectorAll('[data-cart-toggle], .header__cart').forEach((node) => {
      node.setAttribute('aria-label', `${count} ${count === 1 ? 'item' : 'items'} in cart`);
    });
  });

  /* ========================================================================
     5. Sticky header
     ======================================================================== */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'scroll-up';
      this.header = this.querySelector('.header');
      this.isHome = document.body.classList.contains('template--index');
      this.wantsTransparent = this.dataset.transparent === 'true' && this.isHome;
      this.lastY = window.scrollY;
      this.ticking = false;

      if (this.mode !== 'none') this.classList.add('is-sticky');
      if (this.wantsTransparent) this.classList.add('is-transparent');

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      // A dropdown or drawer must never be orphaned by a hiding header.
      document.addEventListener('menu:opened', () => this.reveal());
      document.addEventListener('drawer:opened', () => this.reveal(), true);

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    reveal() {
      this.classList.remove('is-hidden');
    }

    update() {
      const y = Math.max(0, window.scrollY);
      const threshold = this.header ? this.header.offsetHeight : 64;

      this.classList.toggle('is-scrolled', y > 8);
      if (this.wantsTransparent) this.classList.toggle('is-transparent', y <= 8);

      if (this.mode === 'scroll-up' && !Drawers.open && !this.querySelector('details[open]')) {
        const goingDown = y > this.lastY;
        // Only hide once the header is fully out of the reading flow.
        if (goingDown && y > threshold * 2) {
          this.classList.add('is-hidden');
        } else if (!goingDown) {
          this.classList.remove('is-hidden');
        }
      }

      this.lastY = y;
    }
  }

  /* ========================================================================
     6. Header dropdown / mega-menu
     ======================================================================== */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      this.hoverEnabled =
        this.dataset.trigger === 'hover' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.closeTimer = null;

      this.summary.addEventListener('click', () => {
        // Native toggle happens after this handler; report the resulting state.
        setTimeout(() => this.syncAria(), 0);
      });

      this.details.addEventListener('toggle', () => {
        this.syncAria();
        if (this.details.open) {
          HeaderMenu.closeOthers(this);
          document.dispatchEvent(new CustomEvent('menu:opened'));
        }
      });

      if (this.hoverEnabled) {
        this.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
          this.setOpen(true);
        });
        this.addEventListener('mouseleave', () => {
          // Small grace period so a diagonal cursor path does not close it.
          this.closeTimer = setTimeout(() => this.setOpen(false), 120);
        });
      }

      // Closing on focus leaving the subtree covers Tab-out for keyboard users.
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setOpen(false);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          event.stopPropagation();
          this.setOpen(false);
          this.summary.focus();
        }
      });

      HeaderMenu.instances.add(this);
      HeaderMenu.bindOutsideClick();
    }

    disconnectedCallback() {
      HeaderMenu.instances.delete(this);
      clearTimeout(this.closeTimer);
    }

    setOpen(open) {
      if (this.details.open === open) return;
      this.details.open = open;
      this.syncAria();
    }

    syncAria() {
      this.summary.setAttribute('aria-expanded', String(this.details.open));
    }

    static instances = new Set();

    static closeOthers(current) {
      HeaderMenu.instances.forEach((menu) => {
        if (menu !== current) menu.setOpen(false);
      });
    }

    static bindOutsideClick() {
      if (HeaderMenu.bound) return;
      HeaderMenu.bound = true;
      document.addEventListener('click', (event) => {
        if (!event.target.closest('header-menu')) HeaderMenu.closeOthers(null);
      });
    }
  }

  /* ========================================================================
     7. Announcement rotator
     ======================================================================== */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.track || this.slides.length < 2) return;

      this.index = 0;
      this.speed = Number(this.dataset.speed) || 5000;
      this.auto = this.dataset.autorotate === 'true' && !reduceMotion();

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));

      // Pause while the visitor is reading or the tab is in the background.
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      document.addEventListener('visibilitychange', () =>
        document.hidden ? this.stop() : this.start()
      );

      this.render();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      if (!this.auto || this.timer) return;
      this.timer = setInterval(() => this.step(1), this.speed);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    step(delta) {
      this.index = (this.index + delta + this.slides.length) % this.slides.length;
      this.render();
    }

    render() {
      this.track.style.setProperty('--slide-index', this.index);
      this.slides.forEach((slide, i) => {
        slide.toggleAttribute('aria-hidden', i !== this.index);
        // Keep hidden links out of the tab order.
        slide.querySelectorAll('a').forEach((a) => (a.tabIndex = i === this.index ? 0 : -1));
      });
    }
  }

  /* ========================================================================
     8. Predictive search
     ======================================================================== */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.reset = this.querySelector('[data-search-reset]');
      this.enabled = this.dataset.enabled === 'true';
      if (!this.input || !this.results) return;

      this.abort = null;
      this.cache = new Map();

      this.input.addEventListener('input', debounce(() => this.onInput(), 250));
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.reset?.addEventListener('click', () => this.clear());

      this.querySelector('[data-search-form]')?.addEventListener('reset', () => this.clear());
    }

    get term() {
      return this.input.value.trim();
    }

    onInput() {
      this.reset?.toggleAttribute('hidden', this.term.length === 0);
      if (!this.enabled) return;

      if (this.term.length < 2) {
        this.clearResults();
        return;
      }
      this.search(this.term);
    }

    onKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const options = Array.from(this.results.querySelectorAll('[data-predictive-option]'));
      if (!options.length) return;

      event.preventDefault();
      const current = options.indexOf(document.activeElement.closest('[data-predictive-option]'));
      const next =
        event.key === 'ArrowDown'
          ? Math.min(options.length - 1, current + 1)
          : Math.max(0, current - 1);

      const target = options[next];
      (target.querySelector('a') || target).focus();
    }

    async search(term) {
      if (this.cache.has(term)) {
        this.paint(this.cache.get(term));
        return;
      }

      this.abort?.abort();
      this.abort = new AbortController();
      this.setAttribute('data-loading', 'true');

      const types = ['product'];
      if (config.predictiveCollections) types.push('collection');
      if (config.predictiveArticles) types.push('article');

      const params = new URLSearchParams({
        q: term,
        'resources[type]': types.join(','),
        'resources[limit]': '5',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search',
      });

      try {
        const response = await fetch(`${routes.predictiveSearch}?${params}`, {
          signal: this.abort.signal,
        });
        if (!response.ok) throw new Error(response.statusText);

        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const markup = html.querySelector('.predictive, .predictive__empty')?.outerHTML || '';

        this.cache.set(term, markup);
        this.paint(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.clearResults();
      } finally {
        this.removeAttribute('data-loading');
      }
    }

    paint(markup) {
      this.results.innerHTML = markup;
      const count = this.results.querySelectorAll('[data-predictive-option]').length;
      this.input.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
      if (this.status) this.status.textContent = count ? `${count} suggestions` : 'No results';
    }

    clearResults() {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      if (this.status) this.status.textContent = '';
    }

    clear() {
      this.input.value = '';
      this.reset?.setAttribute('hidden', '');
      this.clearResults();
      this.input.focus();
    }
  }

  /* ========================================================================
     9. Cart drawer + line items
     ======================================================================== */
  /**
   * Base class for anything that renders the cart: the drawer and the cart
   * page both extend it. On every cart mutation it swaps its own
   * [data-cart-content] with the server's re-render of its own section, so
   * line prices and totals are never recomputed in JavaScript.
   */
  class CartSection extends HTMLElement {
    connectedCallback() {
      this.sectionName = this.dataset.cartSection;
      this.content = this.querySelector('[data-cart-content]');

      this.onCartUpdated = this.onCartUpdated.bind(this);
      document.addEventListener('cart:updated', this.onCartUpdated);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    onCartUpdated(event) {
      this.replace(event.detail?.sections);
    }

    replace(sections) {
      const markup = sections?.[this.sectionName];
      if (!markup || !this.content) return;

      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const fresh = parsed.querySelector('[data-cart-content]');
      if (!fresh) return;

      this.content.innerHTML = fresh.innerHTML;
      this.classList.toggle('is-empty', Boolean(fresh.querySelector('[data-cart-empty]')));
    }

    setBusy(busy) {
      this.toggleAttribute('data-busy', busy);
      this.setAttribute('aria-busy', String(busy));
    }
  }

  class CartDrawer extends CartSection {
    onCartUpdated(event) {
      // Swap first, then reveal — opening onto stale contents reads as a bug.
      super.onCartUpdated(event);
      if (event.detail?.open) this.openSelf();
    }

    openSelf() {
      Drawers.show('cart', document.querySelector('[data-cart-toggle]'));
    }
  }

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) {
          event.preventDefault();
          this.change(remove.dataset.line, 0);
          return;
        }

        const step = event.target.closest('[data-quantity-step]');
        if (step) {
          event.preventDefault();
          const input = this.input(step.dataset.line);
          if (!input) return;
          const next = Math.max(0, Number(input.value) + Number(step.dataset.quantityStep));
          input.value = next;
          this.change(step.dataset.line, next);
        }
      });

      this.addEventListener(
        'change',
        debounce((event) => {
          const input = event.target.closest('[data-quantity-input]');
          if (!input) return;
          this.change(input.dataset.line, Math.max(0, Number(input.value)));
        }, 300)
      );

      const note = this.closest('[data-cart-content]')?.querySelector('[data-cart-note]');
      note?.addEventListener(
        'change',
        debounce(() => CartAPI.update({ note: note.value }), 500)
      );
    }

    input(line) {
      return this.querySelector(`[data-quantity-input][data-line="${line}"]`);
    }

    async change(line, quantity) {
      const host = this.closest('[data-cart-section]');
      const row = this.querySelector(`[data-cart-item][data-line="${line}"]`);
      const requestedKey = row?.dataset.key;

      host?.setBusy(true);
      row?.classList.add('is-updating');

      try {
        const cart = await CartAPI.change({ line: Number(line), quantity });

        // change.js silently clamps to available stock; surface that to the user.
        const updated = cart.items?.find((item) => item.key === requestedKey);
        if (quantity > 0 && updated && updated.quantity !== quantity) {
          this.showError(line, strings.quantityError?.replace('[quantity]', updated.quantity));
        }

        CartAPI.publish(cart, { sections: cart.sections });
      } catch (error) {
        this.showError(line, error.message || strings.cartError);
        row?.classList.remove('is-updating');
      } finally {
        host?.setBusy(false);
      }
    }

    showError(line, message) {
      const node = this.querySelector(`[data-cart-item][data-line="${line}"] [data-line-error]`);
      if (!node || !message) return;
      node.textContent = message;
      node.removeAttribute('hidden');
    }
  }

  /* ========================================================================
     9b. Quick add
     Delegated at the document, so it covers cards rendered by any section,
     any template, and anything injected later by the Section Rendering API.
     ======================================================================== */
  const QuickAdd = {
    init() {
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quick-add]');
        if (!button || button.disabled) return;
        event.preventDefault();
        // The card title is an overlay anchor covering the whole tile; without
        // this the click would also navigate to the product page.
        event.stopPropagation();
        this.add(button);
      });
    },

    async add(button) {
      const id = Number(button.dataset.variantId);
      if (!id) return;

      const label = button.querySelector('[data-quick-add-label]');
      const original = label?.textContent;

      button.disabled = true;
      button.dataset.state = 'loading';
      if (label) label.textContent = strings.adding || 'Adding';

      try {
        const cart = await CartAPI.add({ id, quantity: 1 });

        button.dataset.state = 'added';
        if (label) label.textContent = strings.added || 'Added';

        CartAPI.publish(cart, { sections: cart.sections, open: true });

        this.reset(button, label, original, 1400);
      } catch (error) {
        button.dataset.state = 'error';
        if (label) label.textContent = error.message || strings.cartError;
        this.reset(button, label, original, 3000);
      }
    },

    reset(button, label, original, delay) {
      clearTimeout(button._resetTimer);
      button._resetTimer = setTimeout(() => {
        delete button.dataset.state;
        button.disabled = false;
        if (label && original) label.textContent = original;
      }, delay);
    },
  };

  /* ========================================================================
     9c. Lazy video
     Autoplaying background video is the single most expensive thing a hero
     can do. This keeps it off the CPU whenever it is not actually visible,
     and off entirely for reduced-motion visitors.
     ======================================================================== */
  class LazyVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.video.playsInline = true;
      this.video.muted = true;
      this.video.loop = true;

      if (reduceMotion()) {
        // Leave the poster frame showing rather than motion nobody asked for.
        this.video.removeAttribute('autoplay');
        this.video.pause();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.play();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? this.play() : this.video.pause()));
        },
        { threshold: 0.15 }
      );
      this.observer.observe(this);

      // A hidden tab should not be decoding frames either.
      this.onVisibility = () => (document.hidden ? this.video.pause() : null);
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    play() {
      // Autoplay can still be refused (low power mode); a rejected promise
      // here is expected, not an error worth surfacing.
      this.video.play().catch(() => {});
    }
  }

  /* ========================================================================
     10. Reveal on scroll
     ======================================================================== */
  const Reveal = {
    init() {
      if (!config.revealOnScroll || reduceMotion() || !('IntersectionObserver' in window)) {
        document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            this.observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
      );

      this.scan();
    },

    scan(root = document) {
      if (!this.observer) {
        root.querySelectorAll?.('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
        return;
      }
      root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
        this.observer.observe(el);
      });
    },
  };

  Theme.reveal = Reveal;

  /* ========================================================================
     11. Localization forms
     ======================================================================== */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-localization-select]');
    if (!select) return;
    select.form?.submit();
  });

  /* ========================================================================
     12. Boot
     ======================================================================== */
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  define('sticky-header', StickyHeader);
  define('header-menu', HeaderMenu);
  define('announcement-rotator', AnnouncementRotator);
  define('predictive-search', PredictiveSearch);
  define('cart-drawer', CartDrawer);
  define('cart-page', CartSection);
  define('cart-items', CartItems);
  define('lazy-video', LazyVideo);

  Drawers.init();
  HeaderMetrics.init();
  Reveal.init();
  QuickAdd.init();

  /* ------------------------------------------------------------------------
     Theme editor: sections are replaced in place, so re-measure and re-scan.
     Custom elements upgrade themselves, which is why there is nothing to
     re-instantiate here.
     ------------------------------------------------------------------------ */
  document.addEventListener('shopify:section:load', (event) => {
    HeaderMetrics.observeTargets();
    HeaderMetrics.measure();
    Reveal.scan(event.target);
  });

  document.addEventListener('shopify:section:unload', () => {
    Drawers.hide();
    HeaderMetrics.measure();
  });

  document.addEventListener('shopify:section:reorder', () => HeaderMetrics.measure());

  // Opening the cart drawer from the editor's own preview controls.
  document.addEventListener('shopify:block:select', (event) => {
    if (event.target.closest('cart-drawer')) Drawers.show('cart');
  });
})();
